import React, { useState, useEffect } from 'react';
import { Modal, Tabs, Card, Descriptions, Button, Divider, Spin, Typography, Carousel, Tag, Table, message, Form, Input, Select } from 'antd';
import './AssetDetailModal.css';
import { InfoCircleOutlined, HistoryOutlined, MoreOutlined, FileImageOutlined, FilePdfOutlined, FileWordOutlined, FileExcelOutlined, FileUnknownOutlined, QrcodeOutlined, AppstoreAddOutlined, ClockCircleOutlined, SettingOutlined, PrinterOutlined } from '@ant-design/icons';
import apiClient from '../services/api';
import { Asset, Company, AssetCategory, Location, Employee } from '../types';
import { formatVND, formatDate, mapStatusToVN, mapApprovalStatusToVN, mapTechnicalConditionToVN } from '../utils/formatters';
import AssetMaintenanceForm from './AssetMaintenanceForm';
import ExportAssetPDFButton from './ExportAssetPDFButton';
import AssetLabelPDF from './AssetLabelPDF';
import { PDFDownloadLink, PDFViewer } from '@react-pdf/renderer';
import { Modal as AntdModal } from 'antd';

// @ts-expect-error: no types for qrcode
import QRCode from 'qrcode';
import AssetAccessories from './AssetAccessories';
import BarcodeBase64 from './BarcodeBase64';
import AuditHistoryTable from './AuditHistoryTable';
import JsBarcode from 'jsbarcode';
import dayjs from 'dayjs';

import AssetInfoTab from './AssetDetailTabs/AssetInfoTab';
import MaintenanceTab from './AssetDetailTabs/MaintenanceTab';
import AuditHistoryTab from './AssetDetailTabs/AuditHistoryTab';
import AccessoriesTab from './AssetDetailTabs/AccessoriesTab';
import AttachmentsTab from './AssetDetailTabs/AttachmentsTab';
import OtherTab from './AssetDetailTabs/OtherTab';
import { useAuth } from '../hooks/useAuth';

const { Title, Text } = Typography;

interface MaintenanceType {
  id: number;
  name: string;
  description?: string;
}

interface AssetDetailModalProps {
  assetId: number | null;
  visible: boolean;
  onClose: () => void;
}





const AssetDetailModal: React.FC<AssetDetailModalProps> = ({ assetId, visible, onClose }) => {
  const { user } = useAuth();
  const [asset, setAsset] = useState<Asset | null>(null);
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [categories, setCategories] = useState<AssetCategory[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [maintenanceTypes, setMaintenanceTypes] = useState<MaintenanceType[]>([]);
  const [suppliers, setSuppliers] = useState([]);
  const [activeTab, setActiveTab] = useState('info');
  const [showMaintenanceForm, setShowMaintenanceForm] = React.useState(false);
  const [maintenanceHistory, setMaintenanceHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showLabelPreview, setShowLabelPreview] = useState(false);



  const [barcodeBase64, setBarcodeBase64] = useState<string | null>(null);
  const [qrCodeBase64, setQrCodeBase64] = useState<string | null>(null);

  // Khai báo biến tạm cho attachments và maintenance_schedules
  const docAttachments = Array.isArray(asset?.attachments) ? asset?.attachments.filter((att: any) => att.attachment_type === 'document') : [];
  const schedules = Array.isArray(asset?.maintenance_schedules) ? asset?.maintenance_schedules : [];

  // Biến an toàn cho typescript
  const safeDocAttachments = docAttachments ?? [];
  const safeSchedules = schedules ?? [];



  const loadMaintenanceHistory = React.useCallback(() => {
    if (!assetId) return;
    setLoadingHistory(true);
    apiClient.getAssetMaintenances(assetId)
      .then((res: any) => setMaintenanceHistory(res.data || res))
      .catch(() => message.error('Không lấy được lịch sử bảo dưỡng'))
      .finally(() => setLoadingHistory(false));
  }, [assetId]);

  useEffect(() => {
    if (visible && assetId) {
      setLoading(true);
      apiClient.getAsset(assetId)
        .then((data: Asset) => setAsset(data))
        .finally(() => setLoading(false));
      apiClient.getCompanies().then((res: any) => setCompanies(res.data || res));
      apiClient.getAssetCategories().then((res: any) => setCategories(res.data || res));
      apiClient.getLocations().then((res: any) => setLocations(res.data || res));
      apiClient.getEmployees().then((res: any) => setEmployees(res.data || res));
      apiClient.getMaintenanceTypes && apiClient.getMaintenanceTypes().then((res: any) => setMaintenanceTypes(res.data || res));
      apiClient.getSuppliers && apiClient.getSuppliers().then((res: any) => setSuppliers(res.data || res));
      loadMaintenanceHistory();
    }
    if (!visible) {
      setAsset(null);
      setMaintenanceHistory([]);
    }
  }, [assetId, visible, loadMaintenanceHistory]);




  // Tạo barcode và QR code base64 khi có asset
  useEffect(() => {
    if (asset) {
      // Tạo barcode base64
      try {
        const canvas = document.createElement('canvas');
        JsBarcode(canvas, asset.asset_code, {
          format: 'CODE128',
          width: 2,
          height: 40,
          fontSize: 12,
          margin: 0,
          displayValue: false,
          background: 'transparent',
          lineColor: '#000'
        });
        setBarcodeBase64(canvas.toDataURL('image/png'));
      } catch (error) {
        // Silent error handling
      }

      // Tạo QR code base64
      const publicUrl = `${window.location.origin}/public/asset/${asset.public_uuid}`;
      QRCode.toDataURL(publicUrl, { width: 128, margin: 0 }, (err: Error | null, url: string) => {
        if (!err) {
          setQrCodeBase64(url);
        }
      });
    }
  }, [asset]);

  const companyName = companies.find(c => c.id === asset?.company_id)?.name || asset?.company_id;
  const categoryName = categories.find(c => c.id === asset?.category_id)?.name || asset?.category_id;
  const locationName = locations.find(l => l.id === asset?.location_id)?.name || asset?.location_id;
  const employeeName = employees.find(e => e.id === asset?.assigned_to_employee_id)?.full_name || asset?.assigned_to_employee_id;
  const supplierName = (suppliers as any[]).find((s: any) => s.id === asset?.supplier_id)?.name || asset?.supplier_id || '—';

  let specs = asset?.technical_specifications;
  if (typeof specs === 'string') {
    try { specs = JSON.parse(specs); } catch { specs = {}; }
  }

  const tabs = [
    {
      label: <span><InfoCircleOutlined style={{ marginRight: 6 }} />Thông tin</span>,
      key: 'info',
      children: (
        <Card variant="borderless" style={{ borderRadius: 12, boxShadow: '0 2px 8px #f0f1f2' }}>
          <Title level={5} style={{ marginBottom: 16, textTransform: 'uppercase' }}>Thông tin chung</Title>
          {/* QR CODE - HIỂN THỊ NGAY DƯỚI TIÊU ĐỀ */}
          <div
            style={{
              background: '#f0f5ff',
              border: '1.5px solid #d9d9d9',
              borderRadius: 10,
              padding: 20,
              marginBottom: 24,
              display: 'flex',
              alignItems: 'center',
              gap: 32,
              boxShadow: '0 2px 8px #e6f7ff'
            }}
          >
            {qrCodeBase64 && (
              <img
                src={qrCodeBase64}
                alt="QR code"
                width={120}
                height={120}
                style={{
                  background: '#fff',
                  border: 'none',
                  boxShadow: '0 2px 8px #e6f7ff'
                }}
              />
            )}
            <div>
              <div style={{ fontWeight: 700, fontSize: 22, color: '#222' }}>{asset?.name}</div>
              <div style={{ color: '#1890ff', fontSize: 18, fontWeight: 600 }}>{asset?.asset_code}</div>
              <div style={{ marginTop: 12 }}>
                <BarcodeBase64
                  value={asset?.asset_code || ''}
                  height={48}
                  displayValue={false}
                  margin={0}
                  style={{ margin: 0, padding: 0 }}
                />
              </div>
            </div>
          </div>
          <Descriptions column={2} size="small" style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Công ty">{companyName}</Descriptions.Item>
            <Descriptions.Item label="Mã tài sản">{asset?.asset_code}</Descriptions.Item>
            <Descriptions.Item label="Tên tài sản">{asset?.name}</Descriptions.Item>
            <Descriptions.Item label="Danh mục">{categoryName}</Descriptions.Item>
            <Descriptions.Item label="Vị trí">{locationName}</Descriptions.Item>
            <Descriptions.Item label="Nhân viên được giao">{employeeName}</Descriptions.Item>
            <Descriptions.Item label="Tình trạng">{mapStatusToVN(asset?.status || '')}</Descriptions.Item>
            <Descriptions.Item label="Tình trạng kỹ thuật">{mapTechnicalConditionToVN(asset?.technical_condition || '')}</Descriptions.Item>
            <Descriptions.Item label="Tài sản cha">{asset?.parent_asset_id || '—'}</Descriptions.Item>
            <Descriptions.Item label="Mô tả">
              {asset?.description ? (
                <div style={{ whiteSpace: 'pre-line' }}>{asset.description}</div>
              ) : '—'}
            </Descriptions.Item>
          </Descriptions>
          <Divider style={{ margin: '16px 0' }} />
          <Title level={5} style={{ marginBottom: 16, textTransform: 'uppercase' }}>Thông số kỹ thuật</Title>
          <Descriptions column={2} size="small" style={{ marginBottom: 16 }}>
            {specs && typeof specs === 'object' && Object.keys(specs).length > 0 ? (
              Object.entries(specs).map(([key, value]) => (
                <Descriptions.Item key={key} label={key}>
                  {value === undefined || value === null || value === ''
                    ? '—'
                    : (typeof value === 'object'
                      ? (Array.isArray(value)
                        ? (value.length > 0 ? JSON.stringify(value) : '—')
                        : (Object.keys(value).length > 0 ? JSON.stringify(value) : '—')
                      )
                      : key.toLowerCase().includes('mô tả')
                        ? <div style={{ whiteSpace: 'pre-line' }}>{String(value)}</div>
                        : String(value)
                    )
                  }
                </Descriptions.Item>
              ))
            ) : (
              <Descriptions.Item label="Thông số kỹ thuật">—</Descriptions.Item>
            )}
          </Descriptions>
          <Divider style={{ margin: '16px 0' }} />
          <Title level={5} style={{ marginBottom: 16, textTransform: 'uppercase' }}>Thông tin mua sắm</Title>
          <Descriptions column={2} size="small" style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Giá trị">{formatVND(asset?.purchase_cost)}</Descriptions.Item>
            <Descriptions.Item label="Ngày mua">{formatDate(asset?.purchase_date)}</Descriptions.Item>
            <Descriptions.Item label="Nhà cung cấp">{supplierName}</Descriptions.Item>
          </Descriptions>
          <Divider style={{ margin: '16px 0' }} />
          <Title level={5} style={{ marginBottom: 16, textTransform: 'uppercase' }}>Bảo hành & kiểm kê</Title>
          <Descriptions column={2} size="small">
            <Descriptions.Item label="Ngày hết hạn bảo hành">{formatDate(asset?.warranty_expiry_date)}</Descriptions.Item>
            <Descriptions.Item label="Ngày kiểm kê gần nhất">{formatDate(asset?.last_audit_date)}</Descriptions.Item>
          </Descriptions>
        </Card>
      )
    },
    {
      label: <span><FileImageOutlined style={{ marginRight: 6 }} />Hồ sơ tài sản</span>,
      key: 'attachments',
      children: (
        <Card variant="borderless" style={{ borderRadius: 12, boxShadow: '0 2px 8px #f0f1f2' }}>
          <Typography.Title level={5} style={{ marginBottom: 16, textTransform: 'uppercase' }}>Hồ sơ tài sản</Typography.Title>
          {safeDocAttachments.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              {safeDocAttachments.map((att: any) => {
                // Nhận diện icon theo mime type hoặc đuôi file
                const ext = att.file_name?.split('.').pop()?.toLowerCase() || '';
                let icon = <FileUnknownOutlined style={{ fontSize: 40, color: '#aaa' }} />;
                if (att.mime_type?.includes('pdf') || ext === 'pdf') icon = <FilePdfOutlined style={{ fontSize: 40, color: '#d4380d' }} />;
                else if (att.mime_type?.includes('word') || ['doc', 'docx'].includes(ext)) icon = <FileWordOutlined style={{ fontSize: 40, color: '#1890ff' }} />;
                else if (att.mime_type?.includes('excel') || ['xls', 'xlsx', 'csv'].includes(ext)) icon = <FileExcelOutlined style={{ fontSize: 40, color: '#52c41a' }} />;
                else if (att.mime_type?.includes('powerpoint') || ['ppt', 'pptx'].includes(ext)) icon = <FileUnknownOutlined style={{ fontSize: 40, color: '#faad14' }} />;
                else if (att.mime_type?.startsWith('image') || ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) icon = <FileImageOutlined style={{ fontSize: 40, color: '#722ed1' }} />;
                else if (['txt', 'md', 'rtf'].includes(ext)) icon = <FileUnknownOutlined style={{ fontSize: 40, color: '#13c2c2' }} />;
                else if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) icon = <FileUnknownOutlined style={{ fontSize: 40, color: '#a0d911' }} />;
                else if (['js', 'ts', 'json', 'xml', 'html', 'css', 'py', 'java', 'c', 'cpp', 'cs', 'php', 'sh', 'bat'].includes(ext)) icon = <FileUnknownOutlined style={{ fontSize: 40, color: '#eb2f96' }} />;
                else if (att.mime_type?.startsWith('audio') || ['mp3', 'wav', 'ogg', 'aac'].includes(ext)) icon = <FileUnknownOutlined style={{ fontSize: 40, color: '#fa541c' }} />;
                else if (att.mime_type?.startsWith('video') || ['mp4', 'avi', 'mov', 'wmv', 'mkv', 'webm'].includes(ext)) icon = <FileUnknownOutlined style={{ fontSize: 40, color: '#2f54eb' }} />;
                return (
                  <div key={att.id} style={{ width: 120, textAlign: 'center' }}>
                    <a href={att.file_path} target="_blank" rel="noopener noreferrer">
                      {icon}
                    </a>
                    <div style={{ fontSize: 13, marginTop: 4, wordBreak: 'break-all' }}>{att.file_name}</div>
                    <a href={att.file_path} download style={{ fontSize: 12, color: '#1890ff' }}>Tải về</a>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )
    },
    {
      label: <span><HistoryOutlined style={{ marginRight: 6 }} />Lịch sử tác nghiệp</span>,
      key: 'maintenance',
      children: (
        <Card variant="borderless" style={{ borderRadius: 12, boxShadow: '0 2px 8px #f0f1f2' }}>
          <Title level={5} style={{ marginBottom: 16, textTransform: 'uppercase' }}><HistoryOutlined /> Lịch sử tác nghiệp</Title>
          <Button type="primary" style={{ marginBottom: 16 }} onClick={() => setShowMaintenanceForm(true)}>Ghi nhận tác nghiệp</Button>
          <Table
            dataSource={maintenanceHistory}
            loading={loadingHistory}
            rowKey="id"
            size="small"
            pagination={false}
            style={{ marginBottom: 16 }}
            columns={[
              {
                title: 'Ngày hoàn thành',
                dataIndex: 'event_date',
                key: 'event_date',
                render: (date: string) => formatDate(date),
              },
              {
                title: 'Loại tác nghiệp',
                dataIndex: 'maintenance_type_id',
                key: 'maintenance_type_id',
                render: (id: number) => maintenanceTypes.find(mt => mt.id === id)?.name || id,
              },
              {
                title: 'Chi phí',
                dataIndex: 'cost',
                key: 'cost',
                render: (cost: number) => formatVND(cost),
              },
              {
                title: 'Người thực hiện',
                dataIndex: 'performed_by_employee_id',
                key: 'performed_by_employee_id',
                render: (id: number) => (employees as any[]).find((e: any) => e.id === id)?.full_name || id || '—',
              },
              {
                title: 'Nhà cung cấp thực hiện',
                dataIndex: 'performed_by_supplier_id',
                key: 'performed_by_supplier_id',
                render: (id: number) => (suppliers as any[]).find((s: any) => s.id === id)?.name || id || '—',
              },
              {
                title: 'Mô tả công việc',
                dataIndex: 'details',
                key: 'details',
                render: (text: string) => text || '—',
              },
            ]}
          />
          <Modal
            open={showMaintenanceForm}
            title="Ghi nhận tác nghiệp"
            onCancel={() => setShowMaintenanceForm(false)}
            footer={null}
            destroyOnHidden
          >
            {asset && (
              <AssetMaintenanceForm
                assetId={asset.id}
                maintenanceTypes={maintenanceTypes}
                suppliers={suppliers}
                employees={employees}
                onSuccess={() => {
                  setShowMaintenanceForm(false);
                  loadMaintenanceHistory(); // Reload bảng sau khi thêm mới
                }}
                onCancel={() => setShowMaintenanceForm(false)}
              />
            )}
          </Modal>
        </Card>
      )
    },
    {
      label: <span><HistoryOutlined style={{ marginRight: 6 }} />Lịch sử kiểm kê</span>,
      key: 'audit_history',
      children: <AuditHistoryTable assetId={assetId!} locations={locations} />
    },
    ...(asset?.accessories && asset.accessories.length > 0 ? [{
      label: <span><AppstoreAddOutlined style={{ marginRight: 6 }} />Vật tư / Thiết bị</span>,
      key: 'accessories',
      children: <AssetAccessories value={asset.accessories} onChange={() => { }} readOnly={true} />,
    }] : []),
    {
      label: <span><MoreOutlined style={{ marginRight: 6 }} />Khác</span>,
      key: 'other',
      children: (
        <Card variant="borderless" style={{ borderRadius: 12, boxShadow: '0 2px 8px #f0f1f2' }}>
          <Title level={5} style={{ marginBottom: 0, textTransform: 'uppercase' }}>Thông tin khác</Title>
          <Descriptions column={2} size="small">
            <Descriptions.Item label="Chu kỳ kiểm kê">{asset?.audit_cycle_months ? asset.audit_cycle_months + ' tháng' : '—'}</Descriptions.Item>
            <Descriptions.Item label="Thời hạn bảo hành">{asset?.warranty_period_months ? asset.warranty_period_months + ' tháng' : '—'}</Descriptions.Item>
            <Descriptions.Item label="Ngày tạo">{formatDate(asset?.created_at)}</Descriptions.Item>
            <Descriptions.Item label="Ngày cập nhật">{formatDate(asset?.updated_at)}</Descriptions.Item>
          </Descriptions>
          {safeSchedules.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <Text strong style={{ textTransform: 'uppercase', fontSize: 15, letterSpacing: 1 }}>Loại bảo dưỡng & chu kỳ:</Text>
              <div style={{ margin: '8px 0 0 0', padding: 0 }}>
                {safeSchedules.map((ms: any, index: number) => {
                  const mt = maintenanceTypes.find(t => t.id === ms.maintenance_type_id);
                  return (
                    <Typography.Paragraph key={ms.id || ms.maintenance_type_id || index} style={{ marginBottom: 4, fontSize: 14 }}>
                      <Typography.Text strong>
                        {mt ? mt.name.toUpperCase() : `LOẠI ID: ${ms.maintenance_type_id}`}
                      </Typography.Text>
                      {ms.cycle_months ? (
                        <span style={{ marginLeft: 8, color: '#888' }}>
                          - Chu kỳ: <Typography.Text code>{ms.cycle_months} tháng</Typography.Text>
                        </span>
                      ) : ''}
                    </Typography.Paragraph>
                  );
                })}
              </div>
            </div>
          )}
        </Card>
      )
    },
  ];



  return (
    <Modal
      open={visible}
      onCancel={onClose}
      onOk={onClose}
      width="min(80vw, 1000px)"
      style={{ maxHeight: '90vh' }}
      styles={{ body: { maxHeight: 'calc(90vh - 120px)', overflowY: 'auto' } }}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {asset && (user?.role !== 'employee_user') && (
              <ExportAssetPDFButton
                asset={asset}
                companies={companies}
                categories={categories}
                locations={locations}
                suppliers={suppliers}
                employees={employees}
                maintenanceTypes={maintenanceTypes}
                qrBase64={qrCodeBase64 || undefined}
                barcodeBase64={barcodeBase64 || undefined}
              />
            )}
            {asset && (user?.role !== 'employee_user') && (
              <Button
                icon={<PrinterOutlined />}
                onClick={() => setShowLabelPreview(true)}
              >
                In tem
              </Button>
            )}

          </div>
          <Button onClick={onClose}>Đóng</Button>
        </div>
      }
      title={
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 700, fontSize: 22, textTransform: 'uppercase' }}>{asset?.name || 'Chi tiết tài sản'}</span>
              {companyName && (
                <Tag color="blue" style={{ fontSize: 13, fontWeight: 500, borderRadius: 8, padding: '0 10px', height: 22, display: 'flex', alignItems: 'center' }}>{companyName}</Tag>
              )}
            </div>
          </div>
          <div style={{ fontWeight: 400, fontSize: 15, color: '#888' }}>{asset?.asset_code}</div>
          {/* Hiển thị hình ảnh tài sản */}
          {asset ? (() => {
            const images = Array.isArray(asset.attachments)
              ? asset.attachments.filter((att: any) => att.attachment_type === 'image')
              : [];
            if (images.length === 0) {
              return <img src="/default_img.jpg" alt="Ảnh tài sản" style={{ width: '100%', height: 320, objectFit: 'contain', borderRadius: 12, background: '#f5f5f5', marginTop: 12 }} draggable={false} />;
            }
            if (images.length === 1) {
              return <img src={images[0].file_path} alt={images[0].file_name} style={{ width: '100%', height: 320, objectFit: 'contain', borderRadius: 12, background: '#f5f5f5', marginTop: 12 }} draggable={false} />;
            }
            return (
              <Carousel dots style={{ width: '100%', marginTop: 12 }}>
                {images.map((img: any) => (
                  <div key={img.id}>
                    <img src={img.file_path} alt={img.file_name} style={{ width: '100%', height: 320, objectFit: 'contain', borderRadius: 12, background: '#f5f5f5' }} draggable={false} />
                  </div>
                ))}
              </Carousel>
            );
          })() : null}
        </div>
      }
    >
      {loading || !asset ? (
        <Spin style={{ display: 'block', margin: '40px auto' }} />
      ) : (
        (() => {
          const accessoriesTabItem = asset?.accessories && asset.accessories.length > 0
            ? [{
              label: <span><AppstoreAddOutlined style={{ marginRight: 6 }} />Vật tư / Thiết bị</span>,
              key: 'accessories',
              children: <AccessoriesTab accessories={asset.accessories} />,
            }]
            : [];
          const items = [
            {
              label: <span><InfoCircleOutlined style={{ marginRight: 6 }} />Thông tin</span>,
              key: 'info',
              children: (
                <AssetInfoTab
                  asset={asset}
                  companies={companies}
                  categories={categories}
                  locations={locations}
                  employees={employees}
                  suppliers={suppliers}
                  qrCodeBase64={qrCodeBase64}
                />
              )
            },
            {
              label: <span><FileImageOutlined style={{ marginRight: 6 }} />Hồ sơ tài sản</span>,
              key: 'attachments',
              children: (
                <AttachmentsTab docAttachments={safeDocAttachments} />
              )
            },
            {
              label: <span><SettingOutlined style={{ marginRight: 6 }} />Lịch sử tác nghiệp</span>,
              key: 'maintenance',
              children: (
                <MaintenanceTab
                  asset={asset}
                  maintenanceHistory={maintenanceHistory}
                  loadingHistory={loadingHistory}
                  maintenanceTypes={maintenanceTypes}
                  suppliers={suppliers}
                  employees={employees}
                  onReload={loadMaintenanceHistory}
                />
              )
            },
            {
              label: <span><ClockCircleOutlined style={{ marginRight: 6 }} />Lịch sử kiểm kê</span>,
              key: 'audit_history',
              children: <AuditHistoryTab assetId={assetId!} locations={locations} />
            },
            ...accessoriesTabItem,
            {
              label: <span><MoreOutlined style={{ marginRight: 6 }} />Khác</span>,
              key: 'other',
              children: (
                <OtherTab
                  asset={asset}
                  safeSchedules={safeSchedules}
                  maintenanceTypes={maintenanceTypes}
                  maintenanceHistory={maintenanceHistory}
                />
              )
            },
          ];
          return (
            <Tabs
              tabPosition="left"
              type="card"
              size="large"
              style={{ minHeight: 500 }}
              activeKey={activeTab}
              onChange={setActiveTab}
              items={items}
            />
          );
        })()
      )}
      {/* Modal xem trước tem tài sản */}
      <AntdModal
        open={showLabelPreview}
        onCancel={() => setShowLabelPreview(false)}
        footer={[
          asset && (
            <PDFDownloadLink
              key="download"
              document={(() => {
                const companyName = companies.find(c => c.id === asset.company_id)?.name || '—';
                const publicUrl = `${window.location.origin}/public/asset/${asset.asset_code}`;
                return (
                  <AssetLabelPDF
                    assetCode={asset.asset_code}
                    assetName={asset.name}
                    publicUrl={publicUrl}
                    companyName={companyName}
                    barcodeBase64={barcodeBase64}
                    qrCodeBase64={qrCodeBase64}
                  />
                );
              })()}
              fileName={`tem_${asset.asset_code}.pdf`}
            >
              {({ loading }) => (
                <Button icon={<FilePdfOutlined />} loading={loading} type="primary">
                  Tải về PDF
                </Button>
              )}
            </PDFDownloadLink>
          ),
          <Button key="close" onClick={() => setShowLabelPreview(false)}>Đóng</Button>
        ].filter(Boolean)}
        width={400}
        title={`Xem trước tem tài sản`}
      >
        {asset && (
          <div style={{ height: 180, border: '1px solid #eee', borderRadius: 8, overflow: 'auto' }}>
            <PDFViewer width="100%" height={160} showToolbar={false} style={{ border: 'none' }}>
              {(() => {
                const companyName = companies.find(c => c.id === asset.company_id)?.name || '—';
                const publicUrl = `${window.location.origin}/public/asset/${asset.asset_code}`;
                return (
                  <AssetLabelPDF
                    assetCode={asset.asset_code}
                    assetName={asset.name}
                    publicUrl={publicUrl}
                    companyName={companyName}
                    barcodeBase64={barcodeBase64}
                    qrCodeBase64={qrCodeBase64}
                  />
                );
              })()}
            </PDFViewer>
          </div>
        )}
      </AntdModal>


    </Modal>
  );
};

export default AssetDetailModal;